import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { fetchToken } from "./Auth";
import "../css/Gantt3.css";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const parseDate = (dateStr) => {
  if (!dateStr || dateStr === "0000-00-00") return null;
  const date = new Date(dateStr);
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  const adjustedDate = new Date(date.getTime() + userTimezoneOffset);

  return isNaN(adjustedDate.getTime()) || adjustedDate.getFullYear() < 1900
    ? null
    : adjustedDate.getTime();
};

const formatDateBr = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

function Gantt3() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [projeto, setProjeto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = fetchToken();

    if (!token) {
      alert("Sessão expirada.");
      navigate("/login");
      return;
    }

    setLoading(true);
    axios
      .get(`/api/projetocompleto/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.Status === "Success" && res.data.Result.length > 0) {
          setProjeto(res.data.Result[0]);
        } else {
          alert("Projeto não encontrado ou acesso negado.");
        }
      })
      .catch((err) => {
        console.error("Erro ao buscar projeto:", err);
        if (
          err.response &&
          (err.response.status === 401 || err.response.status === 403)
        ) {
          alert("Sem permissão.");
          navigate("/projetocompleto");
        }
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // 2. Prepara a configuração do gráfico
  const chartConfig = useMemo(() => {
    if (!projeto) return null;

    // Adicionado o campo "realizada" puxando do banco
    const etapasRaw = [
      {
        label: "1. Estruturação",
        start: projeto.data_inicio_etapa1,
        end: projeto.data_termino_etapa1,
        realizada: projeto.datarealizada_etapa1,
      },
      {
        label: "2. TdR e Orçamento",
        start: projeto.data_inicio_etapa2,
        end: projeto.data_termino_etapa2,
        realizada: projeto.datarealizada_etapa2,
      },
      {
        label: "3. Licitação",
        start: projeto.data_inicio_etapa3,
        end: projeto.data_termino_etapa3,
        realizada: projeto.datarealizada_etapa3,
      },
      {
        label: "4. Contrato",
        start: projeto.data_inicio_etapa4,
        end: projeto.data_termino_etapa4,
        realizada: projeto.datarealizada_etapa4,
      },
      {
        label: "5. Execução",
        start: projeto.data_inicio_etapa5,
        end: projeto.data_termino_etapa5,
        realizada: projeto.datarealizada_etapa5,
      },
      {
        label: "6. Encerramento",
        start: projeto.data_inicio_etapa6,
        end: projeto.data_termino_etapa6,
        realizada: projeto.datarealizada_etapa6,
      },
    ];

    // Converter as datas para milissegundos
    const etapasValidas = etapasRaw
      .map((e) => ({
        ...e,
        startMs: parseDate(e.start),
        endMs: parseDate(e.end),
        realizadaMs: parseDate(e.realizada),
      }))
      .filter((e) => e.startMs && e.endMs && e.endMs >= e.startMs);

    if (etapasValidas.length === 0) return null;

    // Calcular o menor e maior ponto do gráfico incluindo as datas realizadas
    const allDates = etapasValidas.flatMap((e) => {
      const dates = [e.startMs, e.endMs];
      if (e.realizadaMs) dates.push(e.realizadaMs);
      return dates;
    });

    const minDate = Math.min(...allDates);
    const maxDate = Math.max(...allDates);
    const margin = 432000000; // 5 dias de respiro nos cantos

    return {
      data: {
        labels: etapasValidas.map((e) => e.label),
        datasets: [
          // DATASET 1: O Previsto (Usando cores mais fracas/claras)
          {
            label: "Previsto",
            data: etapasValidas.map((e) => [e.startMs, e.endMs]),
            backgroundColor: [
              "#93C5FD", // Azul claro
              "#6EE7B7", // Verde claro
              "#C4B5FD", // Roxo claro
              "#FCD34D", // Amarelo claro
              "#FCA5A5", // Vermelho claro
              "#A5B4FC", // Indigo claro
            ],
            borderWidth: 0,
            barPercentage: 0.9,
            categoryPercentage: 0.8,
            borderRadius: 4,
          },
          // DATASET 2: O Realizado (Usando as suas cores originais vibrantes)
          {
            label: "Realizado",
            // Se não houver realizadaMs, retorna null para não desenhar a barra
            data: etapasValidas.map((e) =>
              e.realizadaMs ? [e.startMs, e.realizadaMs] : null,
            ),
            backgroundColor: [
              "#3B82F6", // Azul forte
              "#10B981", // Verde forte
              "#8B5CF6", // Roxo forte
              "#F59E0B", // Amarelo forte
              "#EF4444", // Vermelho forte
              "#6366F1", // Indigo forte
            ],
            borderWidth: 0,
            barPercentage: 0.9,
            categoryPercentage: 0.8,
            borderRadius: 4,
          },
        ],
      },
      options: {
        indexAxis: "y", // Mantém o gráfico deitado
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { left: 0, right: 20, top: 0, bottom: 0 },
        },
        plugins: {
          legend: {
            display: false, // Mantive falso para não bugar com as múltiplas cores
          },
          tooltip: {
            backgroundColor: "#1e293b",
            padding: 12,
            titleFont: { size: 14 },
            bodyFont: { size: 13 },
            callbacks: {
              title: (ctx) => ctx[0].label,
              label: (ctx) => {
                // Identifica se estamos passando o mouse no Previsto ou no Realizado
                const datasetLabel = ctx.dataset.label;
                if (!ctx.raw) return `${datasetLabel}: Sem dados`;

                const start = ctx.raw[0];
                const end = ctx.raw[1];
                const dias =
                  Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

                return `${datasetLabel}: ${formatDateBr(start)} até ${formatDateBr(end)} (${dias} dias)`;
              },
            },
          },
        },
        scales: {
          x: {
            position: "top",
            min: minDate - margin,
            max: maxDate + margin,
            type: "linear",
            grid: {
              color: "#e2e8f0",
              borderDash: [5, 5],
              drawBorder: false,
            },
            ticks: {
              color: "#64748b",
              font: { size: 11 },
              callback: (value) => formatDateBr(value),
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6,
            },
          },
          y: {
            stacked: false, // Garante que as barras fiquem uma embaixo da outra, não somadas
            grid: { display: false, drawBorder: false },
            ticks: {
              color: "#334155",
              font: { size: 12, weight: "600" },
              padding: 15,
            },
          },
        },
      },
    };
  }, [projeto]);

  if (loading) {
    return <div className="gantt-state">Carregando dados...</div>;
  }

  if (!projeto) {
    return (
      <div className="gantt-state">Projeto não encontrado ou sem acesso.</div>
    );
  }

  return (
    <main className="gantt-page">
      <div className="gantt-container">
        <div className="top-action-bar">
          <Link to="/projetocompleto" className="btn-voltar-big">
            Voltar para Projetos
          </Link>
        </div>

        <header className="gantt-header-center">
          <h1>{projeto.nomeprojeto}</h1>
          <span className="subtitle">Cronograma Físico de Execução</span>
          {/* Legenda manual para o usuário entender as duas barras */}
          <div
            style={{ marginTop: "10px", fontSize: "0.85rem", color: "#64748b" }}
          >
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                backgroundColor: "#93C5FD",
                marginRight: "5px",
                borderRadius: "2px",
              }}
            ></span>{" "}
            Prazo Previsto
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                backgroundColor: "#3B82F6",
                marginLeft: "15px",
                marginRight: "5px",
                borderRadius: "2px",
              }}
            ></span>{" "}
            Realizado
          </div>
        </header>

        <div className="chart-wrapper">
          {chartConfig ? (
            <Bar data={chartConfig.data} options={chartConfig.options} />
          ) : (
            <div className="empty-msg">
              Este projeto ainda não possui datas válidas cadastradas para gerar
              o gráfico.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default Gantt3;
